import { TICK_RATE as CONFIG_TICK_RATE } from '@runkr/config'
import type { Balance, ItemId, RideId } from '@runkr/config'
import type { InputBits } from './inputBits'
import { createRngStreams } from './rng'
import type { RngStreams } from './rng'
import type { CompiledTrack } from './track/types'

/** Simulation ticks per second. */
export const TICK_RATE = CONFIG_TICK_RATE

/** Fixed timestep in seconds. */
export const DT = 1 / TICK_RATE

/** Sentinel for "no tick scheduled" in tick fields that are compared inclusively. */
export const NO_TICK = -1

export type PlayerId = number

/** "No player" in player-index fields (a drone without a target). */
export const NO_PLAYER = -1

export type { ItemId, RideId }

/**
 * One racer (ARCHITECTURE §2.3 + D-019). `x` is the hitbox's left edge and `y` its **bottom**
 * edge (the feet), so the box is `[x, x + w] × [y - h, y]` and sliding shrinks `h` upward.
 * `h` is always the standing height (no slide since D-044).
 * `*Until` fields are exclusive ends (`until > tick` means active) except `coyoteUntil` and
 * `jumpBufferedUntil`, which are the last tick (inclusive) the window is open (DECISIONS O-6).
 */
export interface PlayerState {
  id: PlayerId
  x: number
  y: number
  vx: number
  vy: number
  w: number
  h: number
  grounded: boolean
  /** Air jumps still available this airtime (D-044: one double jump, back on landing). */
  jumpsLeft: number
  coyoteUntil: number
  jumpBufferedUntil: number
  jumpHeldTicks: number
  stunnedUntil: number
  invulnUntil: number
  shieldUntil: number
  boostUntil: number
  item: ItemId | null
  itemCooldownUntil: number
  checkpoint: number
  /** Tick the pending respawn happens, `NO_TICK` when alive. */
  respawnAtTick: number
  progress: number
  placement: number
  finishTick: number
  rank: number
  boostPct: number
  boostIgnoresMud: boolean
  coins: number
  lastHitTick: number
  /** Rides (D-050/D-056): the runner's ride, `null` = none (from the spawn). */
  rideId: RideId | null
  /** Shards on the meter, 0..`chargeShards`. */
  rideCharge: number
  /** Summon wind-up ends (riding starts) at this tick, `NO_TICK` when not summoning. */
  summonAtTick: number
  /** Riding while `tick < ridingUntil` (0 = not riding). */
  ridingUntil: number
  /** Drillmole: underground while `tick < burrowUntil`. */
  burrowUntil: number
  /** Racers (bit per player index) this ride has already bumped (bulldoze, shell). */
  bumpedMask: number
  /** Oil Slick (T3.12): skidding, no control and slow, while `tick < skidUntil`. */
  skidUntil: number
  /** Magnet (T3.12): pulling shards and item boxes in while `tick < magnetUntil`. */
  magnetUntil: number
  lastInputTick: number
  prevBits: InputBits
}

/** Mutable per-platform state. Moving platforms update `x`/`y`; crumbling ones use the ticks. */
export interface PlatformState {
  x: number
  y: number
  /** Tick a crumbling platform stops being solid, `NO_TICK` while untouched. */
  fallAt: number
  /** Tick it becomes solid again. */
  backAt: number
}

export type ProjectileKind = 'spike_ball' | 'homing_drone' | 'leader_missile'

/**
 * A moving item (ARCHITECTURE §2.5), updated in step 5 in array order. Like players, `x` is the
 * left edge and `y` the bottom edge, so the box is `[x, x + w] × [y - h, y]`. `owner` and
 * `target` are player indices. Flat primitives only, so snapshots copy it with a spread.
 */
export interface Projectile {
  /** Stable id from `World.nextId`. */
  id: number
  kind: ProjectileKind
  owner: number
  x: number
  y: number
  w: number
  h: number
  vx: number
  vy: number
  /** Spike ball: resting on terrain (allows the step-up at seams). */
  grounded: boolean
  /** Homing drone: the player it chases, `NO_PLAYER` when it flies straight. */
  target: number
  /** Homing drone: inside `lockPx` horizontally, vertical tracking stopped for good. */
  locked: boolean
  /** Removed at the start of this tick's update (exclusive end). */
  expiresAtTick: number
}

export type TrapKind = 'trip_wire' | 'oil_slick'

/** A dropped item that waits for someone to run into it. Same box convention as `Projectile`. */
export interface Trap {
  id: number
  kind: TrapKind
  owner: number
  x: number
  y: number
  w: number
  h: number
  vy: number
  grounded: boolean
  /** The owner is ignored while `tick < ownerSafeUntil`. */
  ownerSafeUntil: number
  expiresAtTick: number
}

/**
 * Item crate: `x`/`y` is the top-left of a `crateSizePx` box (D-028). Available while
 * `tick >= respawnAtTick` (`NO_TICK` until first taken).
 */
export interface CrateState {
  id: number
  x: number
  y: number
  respawnAtTick: number
}
/** `BotMemory.obstacleId` before the bot has seen anything. */
export const NO_OBSTACLE = -1

/** `BotMemory.upperRoutePct` before the bot's personality is drawn (first `botInput`). */
export const NO_PERSONALITY = -1

/** A move the bot has decided on but not pressed yet (a late slip). */
export type BotAction = 'none' | 'tap' | 'hold'

/**
 * Per-bot memory (T1.4), one entry per player index (humans carry an unused one) so snapshots
 * and determinism cover it. Plain data only.
 */
export interface BotMemory {
  /** The bot acts on `obstacleId` from this tick on (reaction delay drawn once per obstacle). */
  reactAtTick: number
  /** Identity of the obstacle the reaction delay was drawn for. */
  obstacleId: number
  /** Tick the held item becomes due, `NO_TICK` while the slot is empty. */
  itemUseAtTick: number
  /** The jump in progress is a full-height one: keep JUMP down while rising. */
  holdJump: boolean
  /** Homing drone locked on this bot (projectile id) and when the bot reacts to it. */
  droneId: number
  droneReactAtTick: number
  /** Drawn with each obstacle's reaction delay: act this many ticks before the last safe tick. */
  actEarlyTicks: number
  /** Drawn with each obstacle: a late slip of this many ticks past the last safe tick, 0 = none. */
  lateTicks: number
  /** Pending late slip: press `slipAction` at this tick, `NO_TICK` when none is pending. */
  slipAtTick: number
  slipAction: BotAction
  /** Personality: chance (percent) of taking an upper route at a fork, drawn once. */
  upperRoutePct: number
  /** The upper route last decided on (`NO_OBSTACLE` = none) and whether the bot takes it. */
  routeId: number
  routeUp: boolean
  /** A planned double jump: release at the apex, then press again. */
  airJump: boolean
}

export function createBotMemory(): BotMemory {
  return {
    reactAtTick: 0,
    obstacleId: NO_OBSTACLE,
    itemUseAtTick: NO_TICK,
    holdJump: false,
    droneId: NO_OBSTACLE,
    droneReactAtTick: 0,
    actEarlyTicks: 0,
    lateTicks: 0,
    slipAtTick: NO_TICK,
    slipAction: 'none',
    upperRoutePct: NO_PERSONALITY,
    routeId: NO_OBSTACLE,
    routeUp: false,
    airJump: false,
  }
}

export interface PlayerSpawn {
  id: PlayerId
  /** The seat's ride (PHASE3 T3.10); none when omitted. */
  rideId?: RideId | null
}

export interface World {
  tick: number
  readonly cfg: Balance
  readonly track: CompiledTrack
  players: PlayerState[]
  projectiles: Projectile[]
  traps: Trap[]
  crates: CrateState[]
  rng: RngStreams
  finishOrder: PlayerId[]
  platformState: PlatformState[]
  /**
   * Shards taken, per runner (D-056: everyone collects their own copy): index
   * `player * track.coins.length + coin`.
   */
  coinsTaken: boolean[]
  bots: BotMemory[]
  /** Next id for a projectile or trap; ids are never reused within a match. */
  nextId: number
}

export function createPlayer(
  id: PlayerId,
  x: number,
  y: number,
  cfg: Balance,
  rideId: RideId | null = null,
): PlayerState {
  return {
    id,
    x,
    y,
    vx: 0,
    vy: 0,
    w: cfg.playerWidthPx,
    h: cfg.playerHeightPx,
    grounded: false,
    jumpsLeft: 1,
    coyoteUntil: NO_TICK,
    jumpBufferedUntil: NO_TICK,
    jumpHeldTicks: 0,
    stunnedUntil: 0,
    invulnUntil: 0,
    shieldUntil: 0,
    boostUntil: 0,
    item: null,
    itemCooldownUntil: 0,
    checkpoint: 0,
    respawnAtTick: NO_TICK,
    progress: x,
    placement: 0,
    finishTick: 0,
    rank: 0,
    boostPct: 0,
    boostIgnoresMud: false,
    coins: 0,
    lastHitTick: 0,
    rideId,
    rideCharge: 0,
    summonAtTick: NO_TICK,
    ridingUntil: 0,
    burrowUntil: 0,
    bumpedMask: 0,
    skidUntil: 0,
    magnetUntil: 0,
    lastInputTick: 0,
    prevBits: 0,
  }
}

/**
 * Build a fresh world. Runners line up at checkpoint 0, `startSpacingPx` apart going right, in
 * an order rotated by the seed so no player index always starts in front (D-045: with one
 * running speed for everyone, runners on one spot would stay drawn as one).
 */
export function createWorld(
  track: CompiledTrack,
  cfg: Balance,
  seed: number,
  spawns: readonly PlayerSpawn[],
): World {
  const spawn = track.checkpoints[0]
  if (!spawn) throw new Error('createWorld: track has no checkpoint 0 (spawn)')
  const n = spawns.length
  const shift = n > 0 ? ((seed % n) + n) % n : 0
  const slot = (i: number): number => (i + shift) % n
  const players = spawns.map((s, i) =>
    createPlayer(s.id, spawn.x + slot(i) * cfg.startSpacingPx, spawn.y, cfg, s.rideId ?? null),
  )
  players.forEach((p, i) => (p.rank = n - slot(i)))
  return {
    tick: 0,
    cfg,
    track,
    players,
    projectiles: [],
    traps: [],
    crates: track.crates.map((c, i) => ({ id: i, x: c.x, y: c.y, respawnAtTick: NO_TICK })),
    rng: createRngStreams(seed),
    finishOrder: [],
    platformState: track.platforms.map((pl) => ({
      x: pl.x,
      y: pl.y,
      fallAt: NO_TICK,
      backAt: NO_TICK,
    })),
    coinsTaken: players.flatMap(() => track.coins.map(() => false)),
    bots: players.map(() => createBotMemory()),
    nextId: 1,
  }
}
